import { CommonModule } from '@angular/common'
import { Component, DestroyRef, Input, OnChanges, OnInit } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { Router } from '@angular/router'
import { EventLegalService } from '@app/modules/events/services/event-legal.service'
import { PersonaFieldTypes } from '@app/modules/personas/enums/persona-fields-types.enum'
import { BadgeTemplate, Persona, Personas } from '@app/modules/personas/interfaces/persona.interface'
import { PersonaFactoryService } from '@app/modules/personas/models/persona.model'
import { PersonaService } from '@app/modules/personas/services/persona.service'
import { DTCMMissingFieldsDialog } from '@app/modules/shared/components/dialog/DTCM-missing-fields-dialog/dtcm.missing.fields.dialog.component'
import { ToastrService } from '@app/services/toastr.service'
import { NbFormFieldModule, NbSelectModule } from '@nebular/theme'
import { ServerRepresentedEvent } from '../../../models/event.model'

@Component({
  selector: 'ngx-dtcm-section',
  templateUrl: './dtcm-section.component.html',
  styleUrls: ['./dtcm-section.component.scss'],
  imports: [CommonModule, NbSelectModule, NbFormFieldModule, MatIconModule, MatButtonModule, ReactiveFormsModule]
})
export class DtcmSectionComponent implements OnInit, OnChanges {
  /**
   * DTCM section connection form
   */
  form: UntypedFormGroup
  /**
   * Status of the connection
   */
  isTested = false
  /**
   * Persona Id of the selected persona
   */
  personaId: number
  /**
   * The event of concern
   */
  @Input() event: ServerRepresentedEvent
  /**
   * Persona of a previously filled section
   */
  @Input() persona: Persona
  /**
   * Personas in the event
   */
  @Input() personas: Personas
  /**
   * Event code passed from DTCM form
   */
  @Input() eventCode: string
  @Input() email: string
  @Input() permitNo: string

  @Input() sections
  @Input() dtcm
  prices
  priceCode
  sectionId
  price
  section
  selectedSection
  selectedPrice
  connected = false
  isLoading = false

  /**
   * Barcode of the tested section
   */
  barcode: string
  /**
   * Create a new dtcm-section instance
   * @param dtcmService
   * @param fb
   * @param toastrService
   */

  eventId
  selectedPersona
  isSelectedFormValid = true
  missingFields = ''
  dtcmRequiredFields = [
    {
      form_element_type_id: PersonaFieldTypes.FULLNAME,
      name: 'fullname',
      label: 'Full name',
      is_required: 3,
      is_disable_online: false,
      is_disable_onsite: false,
      is_unique: false,
      form_element_values: [],
      weight: null
    },
    {
      form_element_type_id: PersonaFieldTypes.COUNTRY,
      form_element_values: [],
      is_disable_online: false,
      is_disable_onsite: false,
      is_required: 3,
      is_unique: false,
      label: 'Country of residence',
      name: 'country',
      weight: null
    },
    {
      form_element_type_id: PersonaFieldTypes.EMAIL,
      form_element_values: [],
      is_disable_online: false,
      is_disable_onsite: false,
      is_required: 3,
      is_unique: false,
      label: 'E-mail',
      name: 'email',
      weight: null
    },
    {
      form_element_type_id: PersonaFieldTypes.PHONE,
      form_element_values: [],
      is_disable_online: false,
      is_disable_onsite: false,
      is_required: 3,
      is_unique: false,
      label: 'Phone number',
      name: 'phone',
      weight: null
    }
  ]
  constructor(
    private router: Router,
    private dialog: MatDialog,
    private _eventLegalService: EventLegalService,
    private fb: UntypedFormBuilder,
    private toastrService: ToastrService,
    private personaService: PersonaService,
    private pf: PersonaFactoryService,
    private _destroyRef: DestroyRef
  ) {}

  /**
   * Initialize the event from the query parameters
   */
  ngOnInit(): void {
    this.eventId = this.event?.id
    this.initializeForm()
    if (this.persona) {
      this.selectedPersona = this.persona
      this.personaId = this.persona.id
      this.isTested = true
    }
    if (this.personas?.length === 1) {
      this.selectedPersona = this.personas[0]
      this.personaId = this.selectedPersona.id
    }
    if (this.dtcm) {
      this.sectionChanged(this.dtcm.sectionId, true)
      this.connected = true
    } else if (this.sections?.length === 1) {
      if (this.sections) this.sectionChanged(this.sections[0])
    }
    this.form
      .get('email')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((email) => (this.email = email))
  }

  ngOnChanges(field) {
    if (field.eventCode) this.form?.controls['eventCode'].patchValue(field.eventCode.currentValue)
    if (field.email) this.form?.controls['email'].patchValue(field.email.currentValue)
    if (field.permitNo) this.form?.controls['permitNo'].patchValue(field.permitNo.currentValue)
  }
  /**
   * Initialize the DTCM section test connection form
   */
  initializeForm(): void {
    this.form = this.fb.group({
      eventCode: this.dtcm ? this.dtcm.eventCode : this.eventCode,
      triggerOn: 'OnAttended',
      section: this.dtcm ? this.dtcm.eventCode : this.eventCode,
      sectionId: this.dtcm ? this.dtcm.sectionId : '',
      priceTypeCode: this.dtcm ? this.dtcm.priceTypeCode : '',
      priceTypePrice: this.dtcm ? this.dtcm.priceTypePrice : '',
      sellerCode: 'ACETA1',
      email: this.dtcm ? this.dtcm.email : this.email,
      permitNo: this.dtcm ? this.dtcm.permitNo : this.permitNo
    })
  }
  /**
   * Adjust the price as a multiple of 100 of the price field, without showing
   * that adjustment to the user.
   * @param price
   */
  adjustPrice(event): void {
    if (event.target.value < 0) event.target.value = 0
    const price = event.target.value
    const adjustedPrice = price % 1 === 0 ? price * 100 : Math.trunc(price * 100)
    this.form.controls.priceTypePrice.setValue(adjustedPrice.toString())
  }

  /**
   * Set the personaId based on the selected value
   * @param persona_name
   */
  personaChanged(personaName: string) {
    this.selectedPersona = this.personas.find((persona) => persona.name === personaName)
    const persona = this.personas.find((p) => p.name === personaName)
    this.personaId = persona.id
  }

  /**
   * Set the sectionId based on the selected value
   * @param section_id
   */
  sectionChanged(sectionId: number, isConnected = false) {
    const section = this.sections.find((section) => section.id === sectionId)
    if (!section) {
      this.resetSection()
      this.resetPrice()
      return
    }
    this.selectedSection = section
    this.sectionId = section.id
    this.form.controls.section.setValue(section.code)
    this.form.controls.sectionId.setValue(section.id)
    this.prices = section.priceTypes
    if (section.priceTypes.length === 1) {
      this.priceChanged(section.priceTypes[0].code)
    } else if (isConnected) {
      this.priceChanged(this.dtcm.priceTypeCode)
    } else {
      this.priceChanged('')
    }
    this.form.controls.section.updateValueAndValidity()
    this.form.controls.sectionId.updateValueAndValidity()
  }
  /**
   * Set the priceCode based on the selected value
   * @param price_code
   */
  priceChanged(priceCode: string) {
    const price = this.prices.find((price) => price.code === priceCode)
    if (!price) {
      this.resetPrice()
    }
    this.selectedPrice = price
    this.form.controls.priceTypeCode.setValue(price.code)
    this.priceCode = price.code
    this.form.controls.priceTypeCode.updateValueAndValidity()
    this.form.controls.priceTypePrice.setValue(price.price)
    this.form.controls.priceTypePrice.updateValueAndValidity()
  }

  resetSection() {
    this.selectedSection = ''
    this.sectionId = ''
    this.form.controls.section.setValue('')
    this.form.controls.sectionId.setValue('')
    this.form.controls.section.updateValueAndValidity()
    this.form.controls.sectionId.updateValueAndValidity()
  }
  resetPrice() {
    this.prices = this.selectedSection?.priceTypes
    this.selectedPrice = null
    this.form.controls.priceTypeCode.setValue(null)
    this.priceCode = null
    this.form.controls.priceTypeCode.updateValueAndValidity()
    this.form.controls.priceTypePrice.setValue(null)
    this.form.controls.priceTypePrice.updateValueAndValidity()
  }

  showDeletePersonaDialog() {
    const dialogData = {
      headerText: `This persona is missing mandatory fields required by DET`,
      messageText: `Fields: ${this.missingFields}`
    }
    const dialogRef = this.dialog.open(DTCMMissingFieldsDialog, {
      minWidth: '560px',
      panelClass: 'matdialog',
      data: dialogData
    })

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        if (result === 'gotoPersona') {
          this.router.navigate([`/wizard/update/${this.eventId}/${this.selectedPersona.id}`])
        } else if (result === 'addFields') {
          const personaIndex = this.personas.findIndex((persona) => persona.id === this.selectedPersona.id)
          const personaFieldsIDs = this.selectedPersona.form_elements.map((formElment) => formElment.form_element_type_id)
          const missingFields = this.dtcmRequiredFields.filter((value) => !personaFieldsIDs.includes(value.form_element_type_id))
          missingFields.forEach((field) => {
            this.personas[personaIndex].form_elements.push(field)
          })
          this.personas[personaIndex].form_elements.forEach((element, index) => {
            element.weight = this.pf.generateWeight(index, index, this.personas[personaIndex].form_elements)
          })
          this.personaService
            .createFormElements(this.selectedPersona.id, missingFields)
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
              next: () => {
                this.personas[personaIndex].form_elements.push(...missingFields)
                this.onSubmit()
              },
              error: () => {
                this.toastrService.error('Error', 'Something went wrong')
              }
            })
        }
      })
  }
  /**
   * On test connection form submit
   */
  onSubmit(): void {
    const existedFieldsAndNotRequired = this.selectedPersona.form_elements.filter(
      (field) => this.dtcmRequiredFields.map((f) => f.form_element_type_id).includes(field.form_element_type_id) && field.is_required !== 3
    )
    existedFieldsAndNotRequired.forEach((field) => (field.is_required = 3))
    if (!this.isValidDTCMform(this.selectedPersona)) {
      this.showDeletePersonaDialog()
      return
    }
    if (!this.form.value.email) return
    this.isLoading = true
    if (this.form.valid) {
      this._eventLegalService
        .validateDTCMSection(this.form)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(
          (res) => {
            if (res['message'] === 'success') {
              this.isTested = true
              this.form.markAsPristine()
              const checkedSections = { ...this.form.value, personaId: this.personaId }
              this.updateFormWithDTCM(checkedSections)
              this.barcode = res['dtcmData'].barcode
              this.connected = true
              this.isLoading = false
            } else {
              this.isLoading = false
              this.toastrService.error('Failed', 'We couldn’t establish the connection with DTCM, please make sure the info you provided is correct')
            }
          },
          (err) => {
            this.isLoading = false
          }
        )
    } else if (!this.form.controls.email.valid) {
      this.isLoading = false
      this.toastrService.error('Invalid email!', 'Please provide a valid email address')
    } else {
      this.isLoading = false
      this.toastrService.warning('Missing fields!', 'Please fill all the fields and try again')
    }
  }

  /**
   * Delete scetion
   * @TODO this function should be refactored
   */
  deleteSection(section?) {
    if (section) section.target.parentNode.parentNode.parentNode.parentNode.parentNode.remove()
    // Previously added
    if (this.persona) {
      this.persona.options.dtcm = null
      this.personaService
        .updateForm(this.persona)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe((res) => {
          this.toastrService.success('Success', `Section for ${this.persona.name} has been disconnected`)
          this.connected = false
          this.sectionChanged(null)
        })
    }
    // Just added
    else {
      this._eventLegalService.removeCheckedSectionByPersonaId(this.persona?.id)
      this.connected = false
      this.sectionChanged(null)
    }
  }

  isValidDTCMform(personas: Persona) {
    const personaFieldsIDs = personas.form_elements.map((formElment) => formElment.form_element_type_id)
    this.isSelectedFormValid = this.dtcmRequiredFields.every((type) => personaFieldsIDs.includes(type.form_element_type_id))
    this.dtcmRequiredFields
      .filter((type) => !personaFieldsIDs.includes(type.form_element_type_id))
      .forEach((field, index) => {
        this.missingFields = this.missingFields + ` ${index === 0 ? '' : ' - '} ` + field.name
      })
    return this.dtcmRequiredFields.every((type) => personaFieldsIDs.includes(type.form_element_type_id))
  }

  updateFormWithDTCM(section) {
    const persona = { ...this.personas.find((o) => o.id == section.personaId) }
    this.persona = persona
    if (!persona.options.length) persona.options = {}
    persona.options['dtcm'] = section
    const badgeTemplate: BadgeTemplate = {
      topPadding: 70,
      qr: {
        enabled: true,
        showText: true,
        width: 18,
        fontSize: 2.5
      },
      dtcm: {
        enabled: true,
        showText: true,
        height: 5,
        fontSize: 2.5
      },
      persona: {
        enabled: true,
        fontSize: 10,
        lineHeight: 10
      }
    }
    const fields: { [key: string]: any } = {}
    let counter = 0
    persona.form_elements.forEach((element, index) => {
      if (
        [
          PersonaFieldTypes.EMAIL,
          PersonaFieldTypes.PHONE,
          PersonaFieldTypes.FULLNAME,
          PersonaFieldTypes.COUNTRY,
          PersonaFieldTypes.ORGANIZATION,
          PersonaFieldTypes.JOB_TITLE,
          PersonaFieldTypes.NUMERIC,
          PersonaFieldTypes.PLAIN_TEXT
        ].includes(element.form_element_type_id)
      ) {
        fields[element.name] = {
          name: element.name,
          label: element.label,
          ordinal: counter,
          enabled: [PersonaFieldTypes.EMAIL, PersonaFieldTypes.PHONE, PersonaFieldTypes.FULLNAME, PersonaFieldTypes.COUNTRY].includes(element.form_element_type_id) ? true : false,
          fontSize: 4.5,
          lineHeight: 4.5
        }
        counter++
      }
    })
    badgeTemplate.fields = fields
    let personaBadgeTemplate = persona['badge_template']
    if (typeof personaBadgeTemplate === 'string') personaBadgeTemplate = JSON.parse(personaBadgeTemplate)
    personaBadgeTemplate ? (personaBadgeTemplate['dtcm'] = badgeTemplate['dtcm']) : (personaBadgeTemplate = badgeTemplate)
    this.personaService
      .updateForm({ id: persona.id, options: persona.options, badge_template: persona['badge_template'] })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        this.toastrService.success('Success', `Your event connection to DTCM is now active\r\nYou may now close this window`)
      })
  }

  getCapacity() {
    if (this.sections) return this.sections.find((section) => section.id === this.sectionId)?.capacity
  }
}
